import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Native bundle selector for Canton - Smart Robust Version
 */
class BundleSelectorComponent extends HTMLElement {
  connectedCallback() {
    const variantsScript = this.querySelector('[data-bundle-variants]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.slots = Array.from(this.querySelectorAll('.bundle-selector__slot'));
    this.button = this.querySelector('[data-bundle-add]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.totalEl = this.querySelector('[data-bundle-total]') || this.querySelector('[data-bundle-total-price]');

    this.button?.addEventListener('click', this.handleAdd);
    this.querySelectorAll('.bundle-selector__select').forEach((select) =>
      select.addEventListener('change', this.updateTotal)
    );

    this.updateTotal();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleAdd);
    this.querySelectorAll('.bundle-selector__select').forEach((select) =>
      select.removeEventListener('change', this.updateTotal)
    );
  }

  /**
   * Smart Variant Matcher: Finds variant matching all select values regardless of position/order
   */
  #findVariant(slot) {
    const selects = Array.from(slot.querySelectorAll('.bundle-selector__select'));
    if (!selects.length || !this.variants.length) return undefined;

    // Get selected values in lowercase for exact comparison
    const selectedValues = selects.map(s => String(s.value).trim().toLowerCase());

    return this.variants.find((variant) => {
      // Gather variant options values (option1, option2, option3 or options array)
      const variantValues = (variant.options || [variant.option1, variant.option2, variant.option3])
        .filter(Boolean)
        .map(val => String(val).trim().toLowerCase());

      // Every selected value must exist inside variant options
      return selectedValues.every(val => variantValues.includes(val));
    });
  }

  updateTotal = () => {
    if (!this.totalEl) return;
    let total = 0;
    let allFound = true;

    for (const slot of this.slots) {
      const variant = this.#findVariant(slot);
      if (!variant) {
        allFound = false;
        break;
      }
      const price = Number(variant.price) || 0;
      total += price > 10000 ? price / 100 : price;
    }

    if (allFound && total > 0) {
      this.totalEl.textContent = `إجمالي القطع المختارة: ${Math.round(total)} ج.م (الخصم يتطبق تلقائيًا عند الدفع)`;
    } else {
      this.totalEl.textContent = '';
    }
  };

  handleAdd = async () => {
    if (!this.button) return;

    const items = [];

    for (const slot of this.slots) {
      const variant = this.#findVariant(slot);
      
      // If variant found but stock validation is strict, fallback to variant.id
      if (variant && variant.id) {
        items.push({ id: Number(variant.id), quantity: 1 });
      }
    }

    if (items.length < this.slots.length) {
      this.#showError('الاختيار ده مش متاح دلوقتي، جربي مقاس أو لون تاني.');
      return;
    }

    this.button.disabled = true;
    this.#hideError();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const cartUrl = window.Theme?.routes?.cart_url || '/cart';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || data.description || 'تعذّرت إضافة المنتجات للسلة');
      }

      const cartResponse = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bundle-selector-component',
          sourceId: this.dataset.productId || 'bundle-selector',
          itemCount: items.length,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#showError('حصل خطأ أثناء الإضافة للسلة، حاولي تاني.');
    } finally {
      this.button.disabled = false;
    }
  };

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.classList.add('hidden');
  }
}

if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}